// SalaryComponentMaster.js
import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";

export default function SalaryComponentMaster() {
  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [components, setComponents] = useState([
    { name: "Basic", type: "Earning", method: "% of Gross", value: "40%", taxable: true },
    { name: "HRA", type: "Earning", method: "% of Basic", value: "20%", taxable: true },
    { name: "PF", type: "Deduction", method: "% of Basic", value: "12%", taxable: false },
  ]);

  const [form, setForm] = useState({ name: "", type: "Earning", method: "", value: "", taxable: true });

  const handleSave = () => {
    if (editIndex !== null) {
      // update existing
      const updated = [...components];
      updated[editIndex] = form;
      setComponents(updated);
    } else {
      // add new
      setComponents([...components, form]);
    }
    setForm({ name: "", type: "Earning", method: "", value: "", taxable: true });
    setEditIndex(null);
    setOpen(false);
  };

  const handleEdit = (index) => {
    setForm(components[index]);
    setEditIndex(index);
    setOpen(true);
  };

  const handleDelete = (index) => {
    const updated = components.filter((_, i) => i !== index);
    setComponents(updated);
  };

  return (
    <div className="bg-white dark:bg-gray-900 p-4 font-poppins rounded-2xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Salary Component Master</h2>
        <button
          onClick={() => setOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700"
        >
          + Add Component
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Name</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Type</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Method</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Value</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Taxable</th>
              <th className="py-2 px-4 text-center  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {components.map((c, i) => (
              <tr key={i} className="border-b dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-950 dark:hover:text-white">
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{c.name}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{c.type}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{c.method}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{c.value}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{c.taxable ? "Yes" : "No"}</td>
                <td className="px-4 py-2 flex justify-center items-center gap-2">
                  <button
                    onClick={() => handleEdit(i)}
                    className="px-2 py-1 text-blue-500 hover:underline hover:underline-offset-4"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(i)}
                    className="px-2 py-1 text-red-500 hover:underline hover:underline-offset-4"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
                <Dialog.Title className="text-lg font-medium text-gray-900">
                  {editIndex !== null ? "Edit Component" : "Add Component"}
                </Dialog.Title>

                <div className="mt-4 space-y-3">
                  <input
                    type="text"
                    placeholder="Component Name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border p-2 rounded"
                  />

                  <select
                    className="w-full border p-2 rounded"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="Earning">Earning</option>
                    <option value="Deduction">Deduction</option>
                  </select>

                  <input
                    type="text"
                    placeholder="Calculation Method"
                    value={form.method}
                    onChange={(e) => setForm({ ...form, method: e.target.value })}
                    className="w-full border p-2 rounded"
                  />

                  <input
                    type="text"
                    placeholder="Value/Formula"
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    className="w-full border p-2 rounded"
                  />

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.taxable}
                      onChange={(e) => setForm({ ...form, taxable: e.target.checked })}
                    />
                    Taxable
                  </label>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={() => setOpen(false)}
                    className="px-4 py-2 rounded-md border bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 rounded-md bg-blue-600 text-white"
                  >
                    Save
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
